/* Homepage clone of assets/ao-vnext-pdp.js: O-11 compare + O-12 keyboard-tab state.
   The source pointer intent gate is copied, not reimplemented. Only namespaces differ inside the slider engine.
   Added Home adapter: pair reset/caption plus the source-style Shopify section/block lifecycle; no purchase hooks. */
(() => {
  'use strict';

  if (window.AoHomeComparePdp && typeof window.AoHomeComparePdp.scan === 'function') {
    window.AoHomeComparePdp.scan(document);
    return;
  }

  const instances = new WeakMap();

  const createInstance = (root) => {
    if (!root || instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];

    const listen = (target, eventName, handler, options = {}) => {
      target?.addEventListener(eventName, handler, { ...options, signal });
    };

    if (root.matches('[data-ao-home-compare-pdp]')) {
      const range = root.querySelector('[data-ao-home-compare-pdp-range]');
      const stage = root.querySelector('.ao-home-compare-pdp__stage');
      const output = root.querySelector('[data-ao-home-compare-pdp-value]');
      const beforeLabel = root.querySelector('.ao-home-compare-pdp__label--before')?.textContent?.trim() || 'before';
      const afterLabel = root.querySelector('.ao-home-compare-pdp__label--after')?.textContent?.trim() || 'after';
      const render = () => {
        const nextValue = Number(range?.value);
        const value = Number.isFinite(nextValue) ? Math.max(0, Math.min(100, nextValue)) : 50;
        root.style.setProperty('--ao-home-compare-pdp-position', `${value}%`);
        if (output) {
          output.value = `${value}%`;
          output.textContent = `${value}%`;
        }
        range?.setAttribute('aria-valuetext', `${value}% ${beforeLabel}, ${100 - value}% ${afterLabel}`);
      };
      listen(range, 'input', render, { passive: true });
      listen(range, 'change', render, { passive: true });

      /* O-11 touch-intent gate (port of the Home results controller API 3, assets/ao-home-v3-results.js).
         With Pointer Events the root carries [data-ao-home-compare-pdp-touch]; ao-home-compare-pdp.css then sets pointer-events: none on
         the native range, which keeps keyboard and assistive-technology input only. The stage decides per pointer:
         · mouse, primary button: jump to x, capture, drag (and focus the range for the keyboard);
         · touch, pen and unknown types: nothing moves on pointerdown. After more than 8 px of travel the gesture drags only on
           horizontal intent (|dx| > 1.2 × |dy|); otherwise it is dropped and the page scrolls (touch-action: pan-y). A tap
           changes nothing; pointercancel / lostpointercapture end the gesture.
         Position = x inside the range box (the stage padding box, the same box the clip and the divider use), no thumb offset.
         Without Pointer Events the attribute is not set and the native range keeps its own input. */
      if (range && stage && typeof window.PointerEvent === 'function') {
        const INTENT_PX = 8;
        const HORIZONTAL = 1.2;
        let gesture = null;
        root.setAttribute('data-ao-home-compare-pdp-touch', '');
        cleanups.push(() => root.removeAttribute('data-ao-home-compare-pdp-touch'));

        const follow = (event) => {
          let box = range.getBoundingClientRect();
          if (!box.width) box = stage.getBoundingClientRect();
          if (!box.width) return;
          const next = String(Math.max(0, Math.min(100, Math.round(((event.clientX - box.left) / box.width) * 100))));
          if (range.value === next) return;
          range.value = next;
          render();
        };
        const capture = (event) => {
          try { stage.setPointerCapture(event.pointerId); } catch (_error) { /* capture is optional */ }
        };
        const release = (event) => {
          if (gesture && event.pointerId === gesture.id) gesture = null;
        };

        listen(stage, 'pointerdown', (event) => {
          if (!event.isPrimary) return;
          if (event.pointerType === 'mouse') {
            if (event.button !== 0) return;
            event.preventDefault();
            gesture = { id: event.pointerId, drag: true };
            capture(event);
            range.focus({ preventScroll: true });
            follow(event);
            return;
          }
          // Touch, pen and unknown pointer types: record only. Nothing moves until the gesture shows horizontal intent.
          gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, drag: false };
        });

        listen(stage, 'pointermove', (event) => {
          if (!gesture || event.pointerId !== gesture.id) return;
          if (!gesture.drag) {
            const dx = Math.abs(event.clientX - gesture.x);
            const dy = Math.abs(event.clientY - gesture.y);
            if (dx <= INTENT_PX && dy <= INTENT_PX) return;
            if (dx <= INTENT_PX || dx <= HORIZONTAL * dy) {
              gesture = null; // vertical or diagonal intent: the page scrolls natively
              return;
            }
            gesture.drag = true;
            capture(event);
          }
          if (event.cancelable) event.preventDefault();
          follow(event);
        });

        listen(stage, 'pointerup', release);
        listen(stage, 'pointercancel', release);
        // lostpointercapture bubbles: a child losing its implicit capture must not end the stage gesture.
        listen(stage, 'lostpointercapture', (event) => {
          if (event.target === stage) release(event);
        });
        // A mouse drag must never start a native image drag (which would cancel the pointer stream).
        listen(stage, 'dragstart', (event) => event.preventDefault());
      }
      // Home pair adapter: each newly selected pair starts at its saved position; native input remains keyboard accessible.
      root.aoHomeComparePdpReset = () => {
        if (range) range.value = range.defaultValue;
        render();
      };
      cleanups.push(() => { delete root.aoHomeComparePdpReset; });
      render();
    }

    if (root.matches('[data-ao-home-compare-pdp-switcher]')) {
      const tabs = [...root.querySelectorAll('[data-ao-home-compare-pdp-tab]')];
      const panels = [...root.querySelectorAll('[data-ao-home-compare-pdp-panel]')];
      const caption = root.closest('.ao-home-v5-results')?.querySelector('[data-ao-home-v5-pair-name]');
      let currentKey = null;
      const activate = (key, focus = false) => {
        const activeTab = tabs.find((tab) => tab.dataset.aoHomeComparePdpTab === key);
        const activePanel = panels.find((panel) => panel.dataset.aoHomeComparePdpPanel === key);
        if (!activeTab || !activePanel) return;
        tabs.forEach((tab) => {
          const active = tab === activeTab;
          tab.classList.toggle('ao-home-compare-pdp__tab--active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel === activePanel;
          panel.classList.toggle('ao-home-compare-pdp__panel--active', active);
          panel.hidden = !active;
        });
        if (currentKey !== key) {
          activePanel.aoHomeComparePdpReset?.();
          if (caption) caption.textContent = activePanel.dataset.caption || activeTab.textContent.trim();
          currentKey = key;
        }
        if (focus) activeTab.focus();
      };
      tabs.forEach((tab, index) => {
        listen(tab, 'click', () => activate(tab.dataset.aoHomeComparePdpTab));
        listen(tab, 'keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          else next = (index + 1) % tabs.length;
          activate(tabs[next].dataset.aoHomeComparePdpTab, true);
        });
      });
      const initialTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
      if (initialTab) activate(initialTab.dataset.aoHomeComparePdpTab);
    }

    instances.set(root, {
      destroy() {
        controller.abort();
        cleanups.forEach((cleanup) => cleanup());
        instances.delete(root);
      }
    });
  };

  const selector = '[data-ao-home-compare-pdp], [data-ao-home-compare-pdp-switcher]';
  const scan = (scope = document) => {
    if (scope.matches?.(selector)) createInstance(scope);
    scope.querySelectorAll?.(selector).forEach(createInstance);
  };
  const destroyWithin = (scope) => {
    if (!scope) return;
    const descendants = scope.querySelectorAll ? [...scope.querySelectorAll(selector)] : [];
    [scope, ...descendants].forEach((root) => instances.get(root)?.destroy());
  };

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const chapter = target?.closest('.ao-home-v5-results');
    const blockId = event.detail?.blockId;
    // The same merchant pair also has a gallery tile; editor selection may target that tile instead of its panel.
    const panel = target?.closest('[data-ao-home-compare-pdp-panel]') ||
      (blockId && chapter?.querySelector(`[data-ao-home-compare-pdp-block="${CSS.escape(blockId)}"]`));
    const switcher = panel?.closest('[data-ao-home-compare-pdp-switcher]');
    if (panel && switcher) {
      switcher.querySelector(`[data-ao-home-compare-pdp-tab="${CSS.escape(panel.dataset.aoHomeComparePdpPanel)}"]`)?.click();
    }
  });
  window.AoHomeComparePdp = { scan, destroyWithin };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
